import { EventType } from "@/analytics/analytics-events";
import { loadArea } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectMutationForNewRoomSection } from "@/modes/sheet-mode/edit-waypoints/edit-waypoints-store";
import { selectAreaFor } from "@/store/selections-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import {
  CopyIcon,
  FaroIconButton,
  FaroMenuItem,
  FaroText,
  FaroTooltip,
  HelpIcon,
  TranslateVar,
  useToast,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert, generateGUID } from "@faro-lotv/foundation";
import {
  GUID,
  IElement,
  isIElementAreaSection,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import {
  addToLoadingRoots,
  removeFromLoadingRoots,
  selectAllIElementsOfType,
  selectIElement,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, type JSX } from "react";
import { Vector3 } from "three";
import { ContextMenuAction, ContextMenuActionType } from "../action-types";
import { selectIsAreaLoaded } from "../utils";

type CopyAsLocationMarkerComponentProps = {
  /** The ID of the element to copy */
  elementId: GUID;
  /** Whether to use dark mode styles */
  dark?: boolean;
  /** Optional callback to close the context menu */
  closeContextMenu?(): void;
};

/**
 * @returns An action component to copy an element as a location marker
 */
export function CopyAsLocationMarkerComponent({
  elementId,
  dark,
  closeContextMenu,
}: CopyAsLocationMarkerComponentProps): JSX.Element | null {
  const element = useAppSelector(selectIElement(elementId));
  const currentArea = useAppSelector(selectAreaFor(element));
  const allAreas = useAppSelector(
    selectAllIElementsOfType(isIElementAreaSection),
    isEqual,
  );

  const otherAreas = useMemo(() => {
    if (!currentArea) return allAreas;
    return allAreas.filter((area) => area.id !== currentArea.id);
  }, [allAreas, currentArea]);

  const copyToAnotherArea = useCopyAsLocationMarkerCallback();

  const onAreaSelected = useCallback(
    (areaId: GUID) => {
      copyToAnotherArea(element, areaId);
      closeContextMenu?.();
    },
    [closeContextMenu, element, copyToAnotherArea],
  );

  if (!currentArea || otherAreas.length === 0) {
    return null;
  }

  return (
    <FaroMenuItem
      dark={dark}
      label="Copy as location marker"
      aria-label="Copy as location marker"
      Icon={CopyIcon}
    >
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", px: 1.5 }}
      >
        <FaroText
          variant="labelS"
          sx={{ textTransform: "uppercase" }}
          dark={dark}
        >
          select area
        </FaroText>
        <FaroTooltip title="Select the area to create the new location marker at the same position of the existing waypoint.">
          <FaroIconButton dark={dark} size="xs">
            <HelpIcon />
          </FaroIconButton>
        </FaroTooltip>
      </Stack>
      {otherAreas.map((area) => (
        <FaroMenuItem
          key={area.id}
          dark={dark}
          label={area.name}
          onClick={() => onAreaSelected(area.id)}
        />
      ))}
    </FaroMenuItem>
  );
}

export const COPY_AS_LOCATION_MARKER_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.copyAsLocationMarker,
  CustomComponent: CopyAsLocationMarkerComponent,
};

type CopyAsLocationMarkerCallback = (
  /** The element to copy */
  element: IElement | undefined,
  /** ID of the destination area */
  destAreaId: GUID,
) => Promise<void>;

function useCopyAsLocationMarkerCallback(): CopyAsLocationMarkerCallback {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithDialog } = useErrorHandlers();
  const { openToast } = useToast();

  return useCallback(
    async (element: IElement | undefined, destAreaId: GUID) => {
      Analytics.track(EventType.copyWaypointToAnotherArea);
      try {
        assert(
          element && isIElementRoomSection(element),
          "Element must be defined if this callback is issued, and it must be a room section",
        );
        const destArea = selectIElement(destAreaId)(store.getState());
        assert(
          !!destArea && isIElementAreaSection(destArea),
          "Destination area must exist",
        );
        assert(element.pose, "Room section pose must be defined");
        assert(element.pose.pos, "Room section position must be defined");

        const worldPos = selectIElementWorldPosition(element.id)(
          store.getState(),
        );

        // If the area is not loaded, load it first
        const isLoaded = selectIsAreaLoaded(destArea, store.getState());
        if (!isLoaded) {
          await loadArea(destArea.id, projectApiClient, dispatch);
        }

        const newMutation = selectMutationForNewRoomSection({
          id: generateGUID(),
          name: element.name,
          worldPos: new Vector3(worldPos[0], worldPos[1], worldPos[2]),
          areaId: destAreaId,
        })(store.getState());

        dispatch(addToLoadingRoots(destArea.id));

        await projectApiClient.applyMutations([newMutation]);

        dispatch(removeFromLoadingRoots(destArea.id));

        // Fetch the changed sub-tree and update the local copy of the project
        await dispatch(
          updateProject({
            projectApi: projectApiClient,
            iElementQuery: {
              ancestorIds: [destArea.id],
            },
          }),
        );

        openToast({
          title: (
            <>
              '<TranslateVar name="Waypoint name">{element.name}</TranslateVar>'
              correctly copied to area '
              <TranslateVar name="Area name">{destArea.name}</TranslateVar>'
            </>
          ),
        });
      } catch (error) {
        handleErrorWithDialog({
          title: "Unable to copy the waypoint to another area",
          error,
        });
      }
    },
    [store, dispatch, projectApiClient, handleErrorWithDialog, openToast],
  );
}
